import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Trash, ArrowCounterClockwise, Warning, CircleNotch, FileText, TrashSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_LABELS = {
  quotations: 'Quotation', invoices: 'Invoice', purchase_orders: 'Purchase Order',
  grns: 'GRN', payment_vouchers: 'Payment Voucher', receipt_vouchers: 'Receipt Voucher',
  reports: 'Report', employees: 'Employee', journal_entries: 'Voided Journal Entry',
};

export default function RecycleBinPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`);
        setUser(res.data);
      } catch {
        const u = JSON.parse(localStorage.getItem('erp_user') || '{}');
        setUser(u);
      }
    };
    fetchUser();
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchBin = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType ? `?document_type=${filterType}` : '';
      const res = await axios.get(`${API_URL}/api/workflow/recycle-bin${params}`);
      setItems(res.data);
    } catch { toast.error('Failed to load recycle bin'); }
    setLoading(false);
  }, [filterType]);

  useEffect(() => { fetchBin(); }, [fetchBin]);

  const handleRestore = async (item) => {
    try {
      await axios.post(`${API_URL}/api/workflow/restore/${item.collection_type}/${item.id}`);
      toast.success(`${item.document_number || item.report_number} restored`);
      fetchBin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDelete = async (item) => {
    if (!window.confirm(`Permanently delete ${item.document_number || item.report_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/workflow/recycle-bin/${item.collection_type}/${item.id}/permanent`);
      toast.success('Permanently deleted');
      fetchBin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="recycle-bin-page">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-red-500/10">
            <Trash size={22} className="text-red-500" weight="duotone" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="recycle-bin-title">Recycle Bin</h1>
            <p className="text-sm text-muted-foreground">Deleted documents can be restored or permanently removed by admin</p>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="filter-doc-type">
          <option value="">All Document Types</option>
          {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Badge variant="outline" className="ml-auto">{items.length} item{items.length !== 1 ? 's' : ''}</Badge>
      </div>

      {/* Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Document</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground hidden md:table-cell">Details</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground hidden lg:table-cell">Deleted On</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground hidden lg:table-cell">Deleted By</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-bold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  <CircleNotch size={24} className="animate-spin mx-auto mb-2" />Loading...
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  <TrashSimple size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Recycle bin is empty</p>
                </td></tr>
              ) : items.map(item => (
                <tr key={`${item.collection_type}-${item.id}`} className="hover:bg-muted/30 transition-colors" data-testid={`bin-item-${item.document_number || item.report_number}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono font-medium">{item.document_number || item.report_number}</span>
                    {item.revision && item.revision !== 'R0' && (
                      <Badge variant="outline" className="ml-2 text-[10px]">{item.revision}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{DOC_TYPE_LABELS[item.collection_type] || item.collection_type}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground max-w-[320px]">
                    <div className="truncate">{item.customer_name || item.subject || item.report_type || '-'}</div>
                    <div className="text-[11px] text-amber-700 dark:text-amber-400 whitespace-normal" data-testid={`bin-reason-${item.id}`}>
                      Reason: {item.delete_reason || 'not recorded'}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                    {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs">{item.deleted_by_name || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {item.restorable === false && (
                        <span className="text-xs text-muted-foreground" data-testid={`bin-audit-only-${item.id}`}>Kept for audit</span>
                      )}
                      {isAdmin && item.restorable !== false && (
                        <>
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => handleRestore(item)} data-testid={`restore-${item.document_number || item.report_number}`}>
                            <ArrowCounterClockwise size={14} /> Restore
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handlePermanentDelete(item)} data-testid={`perm-delete-${item.document_number || item.report_number}`}>
                            <Trash size={15} weight="fill" />
                          </Button>
                        </>
                      )}
                      {!isAdmin && item.restorable !== false && (
                        <span className="text-xs text-muted-foreground">Contact admin to restore</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
